import { Link } from '@inertiajs/react';
import { ApplicationPreview } from './application-preview';
import type {
    ApplicationScreen,
    OpenWelcomeDialog,
    WelcomeAction,
} from './data';
type Props = {
    primary: WelcomeAction;
    seekTo: (value: number) => void;
    next: () => void;
    openDialog: OpenWelcomeDialog;
    screen: ApplicationScreen;
    selectScreen: (screen: ApplicationScreen) => void;
};
export function WelcomeExperience({
    primary,
    seekTo,
    next,
    openDialog,
    screen,
    selectScreen,
}: Props) {
    return (
        <main tabIndex={-1} id="experience" className="experience-rail">
            <div className="experience-stage">
                <div className="ambient-plane" aria-hidden="true"></div>
                <div className="hero-backdrop" aria-hidden="true">
                    <img
                        src="/assets/welcome/hero-penpot.png"
                        width="1171"
                        height="781"
                        alt=""
                        fetchPriority="high"
                    />
                    <div className="hero-gradient"></div>
                </div>
                <section
                    className="scene scene-hero is-active"
                    id="inicio"
                    aria-labelledby="hero-title"
                    data-scene="0"
                >
                    <div className="hero-copy reading-plane">
                        <h1 id="hero-title">
                            Menos tempo com nota mais no, {' '}
                            <span className="hero-title-accent">
                                seu negócio.
                            </span>
                        </h1>
                        <p className="intro">
                            Emita e organize suas NFS-e. Automatize as que se
                            repetem.
                        </p>
                        <div className="actions">
                            <Link
                                className="button"
                                href={primary.href}
                                data-app-link="register"
                            >
                                {primary.label}{' '}
                                <span aria-hidden="true">↗</span>
                            </Link>
                            <button
                                className="text-link"
                                type="button"
                                data-dialog="plans-dialog"
                                onClick={(event) =>
                                    openDialog(
                                        'plans-dialog',
                                        event.currentTarget,
                                    )
                                }
                            >
                                Conhecer os planos
                            </button>
                        </div>
                        <p className="audience">
                            Para prestadores de serviço e escritórios contábeis.
                        </p>
                    </div>
                    <div className="hero-capabilities" aria-label="Recursos">
                        <span>Emita NFS-e</span>
                        <span>Gerencie empresas</span>
                        <span>Automatize suas notas</span>
                    </div>
                </section>

                <section
                    className="scene scene-routine"
                    aria-labelledby="routine-title"
                    data-scene="1"
                    aria-hidden="true"
                    inert={true}
                >
                    <div className="routine-copy reading-plane">
                        <h2 id="routine-title">
                            Todas as suas notas
                            <br />
                            num só lugar.
                        </h2>
                        <p className="intro">
                            Alterne entre empresas, encontre qualquer nota por
                            tomador ou período
                            <br className="desktop-break" /> e mande PDF e XML
                            ao cliente sem sair da Servion.
                        </p>
                        <button
                            className="text-link arrow-link"
                            type="button"
                            data-seek="1"
                            onClick={(event) => {
                                event.preventDefault();
                                seekTo(1);
                            }}
                        >
                            Conhecer a plataforma{' '}
                            <span aria-hidden="true">↘</span>
                        </button>
                    </div>
                    <div className="routine-details reading-plane">
                        <article>
                            <p className="eyebrow">
                                <span className="feature-number">01</span>
                            </p>
                            <h3>Emissão em etapas.</h3>
                            <p>
                                <span className="detail-full">
                                    Faltou algum dado do tomador? Salve como
                                    rascunho e termine depois.
                                </span>
                                <span className="detail-compact">
                                    Salve rascunho e termine depois.
                                </span>
                            </p>
                        </article>
                        <article>
                            <p className="eyebrow">
                                <span className="feature-number">02</span>
                            </p>
                            <h3>Todas as empresas numa conta.</h3>
                            <p>
                                <span className="detail-full">
                                    Alterne entre empresas e filtre as notas por
                                    tomador, período ou situação.
                                </span>
                                <span className="detail-compact">
                                    Filtros e troca de empresa.
                                </span>
                            </p>
                        </article>
                        <article>
                            <p className="eyebrow">
                                <span className="feature-number">03</span>
                            </p>
                            <h3>PDF e XML para o cliente.</h3>
                            <p>
                                <span className="detail-full">
                                    Baixe os arquivos ou envie por e-mail
                                    direto da nota emitida.
                                </span>
                                <span className="detail-compact">
                                    Baixe ou envie por e-mail.
                                </span>
                            </p>
                        </article>
                    </div>
                </section>

                <section
                    className="scene scene-automation"
                    aria-labelledby="automation-title"
                    data-scene="2"
                    aria-hidden="true"
                    inert={true}
                >
                    <div className="automation-lead reading-plane">
                        <h2 id="automation-title">Suas notas, automáticas.</h2>
                    </div>
                    <div className="automation-body reading-plane">
                        <p className="intro">
                            Cadastre a nota uma vez e escolha a frequência. A
                            Servion emite nas datas programadas ou deixa um
                            rascunho pronto para você revisar.
                        </p>
                        <button
                            className="text-link arrow-link"
                            type="button"
                            data-seek="1"
                            onClick={(event) => {
                                event.preventDefault();
                                seekTo(1);
                            }}
                        >
                            Veja como funciona{' '}
                            <span aria-hidden="true">↘</span>
                        </button>
                    </div>
                    <div className="routine-details automation-process reading-plane">
                        <article>
                            <p className="eyebrow">
                                <span className="feature-number">01</span>
                            </p>
                            <h3>Cadastre uma vez.</h3>
                            <p>
                                <span className="detail-full">
                                    Escolha a nota, a frequência e se ela sai
                                    automática ou espera sua revisão.
                                </span>
                                <span className="detail-compact">
                                    Nota, frequência e modo de emissão.
                                </span>
                            </p>
                        </article>
                        <article>
                            <p className="eyebrow">
                                <span className="feature-number">02</span>
                            </p>
                            <h3>Emitida na data.</h3>
                            <p>
                                <span className="detail-full">
                                    A nota sai no dia programado, sem você
                                    confirmar uma por uma.
                                </span>
                                <span className="detail-compact">
                                    Sai no dia programado.
                                </span>
                            </p>
                        </article>
                        <article>
                            <p className="eyebrow">
                                <span className="feature-number">03</span>
                            </p>
                            <h3>Pause quando quiser.</h3>
                            <p>
                                <span className="detail-full">
                                    Veja as próximas emissões e pause a
                                    recorrência se o serviço parar.
                                </span>
                                <span className="detail-compact">
                                    Veja as datas ou pause.
                                </span>
                            </p>
                        </article>
                    </div>
                </section>

                <section
                    className="scene scene-product"
                    id="produto"
                    aria-labelledby="product-title"
                    data-scene="3"
                    aria-hidden="true"
                    inert={true}
                >
                    <div className="product-heading reading-plane">
                        <h2 id="product-title">
                            Assim é a Servion
                            <br />
                            de verdade.
                        </h2>
                        <p className="intro">
                            Escolha a frequência, confira as próximas datas e
                            ative a emissão automática.
                        </p>
                    </div>
                    <ApplicationPreview
                        screen={screen}
                        selectScreen={selectScreen}
                        openDialog={openDialog}
                    />
                    <div className="product-actions">
                        <Link
                            className="button"
                            href={primary.href}
                            data-app-link="register"
                        >
                            {primary.label === 'Criar conta'
                                ? 'Começar com a Servion'
                                : primary.label}{' '}
                            <span aria-hidden="true">↗</span>
                        </Link>
                        <button
                            type="button"
                            className="text-link"
                            data-dialog="plans-dialog"
                            onClick={(event) =>
                                openDialog('plans-dialog', event.currentTarget)
                            }
                        >
                            Ver planos
                        </button>
                    </div>
                </section>

                <div
                    className="experience-footer"
                    role="group"
                    aria-label="Continue explorando a Servion"
                >
                    <div className="chapter-caption">
                        <span id="chapter-description">Início</span>
                    </div>
                    <div className="experience-controls">
                        <button
                            id="next-chapter"
                            onClick={next}
                            className="text-link next-chapter"
                            type="button"
                        >
                            Explore a Servion <span aria-hidden="true">↓</span>
                        </button>
                    </div>
                </div>
            </div>
        </main>
    );
}
